import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { serverMessage } from "@/lib/apiError";

import {
  requestProfessionalInvitePhoneCode,
  verifyProfessionalInvitePhoneCode,
} from "@/services/authService";

/** One minute between code requests, as the server also enforces. */
const PHONE_RESEND_COOLDOWN_MS = 60_000;

export interface ProfessionalInvitePhone {
  phone: string;
  otp: string;
  verified: boolean;
  codeSent: boolean;
  sending: boolean;
  verifying: boolean;
  resendSecondsLeft: number;
  setOtp: (value: string) => void;
  onPhoneChange: (value: string) => void;
  sendCode: (channel?: "sms" | "call") => Promise<void>;
  verifyCode: () => Promise<void>;
  /** Back to "enter a number", used by the change-number affordance. */
  reset: () => void;
  /**
   * Forget the number entirely, including a completed verification.
   *
   * Distinct from `reset`: that one keeps the number and re-asks for a code,
   * this one is for when the IDENTITY changed underneath — a verification
   * belonging to a different person must not survive.
   */
  clear: () => void;
  hydrate: (phone: string, verified: boolean) => void;
}

/**
 * The phone gate's state, for any profession's acceptance wizard.
 *
 * WHY A HOOK AND NOT PROPS. This is seven pieces of state, two API calls, a
 * countdown driven by an interval, and three sub-states the UI switches
 * between — threading that through a presentational component would mean
 * twenty props and the state still living in whichever page happened to own
 * it. Every acceptance flow needs exactly this, so it owns itself.
 *
 * THE SERVICE IS ALREADY PROFESSION-AGNOSTIC: requestProfessionalInvitePhoneCode
 * resolves the profession from the token, so nothing here names one.
 *
 * `onProgress` IS INJECTED rather than assumed, because what a flow persists
 * and where differs. The notary page keeps a merged sessionStorage row so a
 * reloaded tab resumes; the accountant page does not. The hook reports what
 * changed and lets the caller decide whether to write it down.
 *
 * SO IS `mapRefusal`, AND FOR A SHARPER REASON. Both phone endpoints can
 * refuse for a reason that belongs to the PROFESSION rather than to the
 * phone: a document-lookup invite whose manual review was declined, or one
 * whose certificate is not uploaded yet, comes back from the SMS endpoint
 * with that error code. Those have nothing to do with telephony and the
 * generic "no pudimos enviar el SMS" actively misleads — the applicant
 * retries a code that will never be sent. The hook cannot name them itself
 * without knowing every strategy's refusals, so the flow that knows its own
 * supplies them, and anything it does not claim falls through to the phone
 * messages below.
 */
export function useProfessionalInvitePhone(options: {
  token: string | null | undefined;
  onProgress?: (patch: { phone: string; phoneVerified: boolean }) => void;
  /** Return a message for a refusal that is about the invitation, not the phone. */
  mapRefusal?: (errorCode: string | undefined) => string | null;
}): ProfessionalInvitePhone {
  const { token, onProgress, mapRefusal } = options;
  const { t } = useTranslation();

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendReadyAt, setResendReadyAt] = useState<number | null>(null);
  const [, setTick] = useState(0);

  // Re-renders once a second while a cooldown is running, so the countdown
  // on the resend button actually counts down. It stops itself at zero.
  useEffect(() => {
    if (resendReadyAt == null || resendReadyAt <= Date.now()) return;
    const id = window.setInterval(() => {
      setTick((n) => n + 1);
      if (Date.now() >= resendReadyAt) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendReadyAt]);

  const resendSecondsLeft =
    resendReadyAt != null && resendReadyAt > Date.now()
      ? Math.ceil((resendReadyAt - Date.now()) / 1000)
      : 0;

  const onPhoneChange = (value: string): void => {
    setPhone(value);
    setVerified(false);
    setOtp("");
    setCodeSent(false);
    setResendReadyAt(null);
    /*
      The stored verification belonged to the OLD number. Leaving it behind
      would restore a reloaded tab past a gate this number never passed.
    */
    onProgress?.({ phone: value, phoneVerified: false });
  };

  const sendCode = async (channel: "sms" | "call" = "sms"): Promise<void> => {
    if (!token) return;
    const trimmed = phone.trim();
    if (trimmed.length < 8) {
      toast.error(t("auth.acceptProfessionalInvitation.phoneInvalid"));
      return;
    }
    setSending(true);
    try {
      const result = await requestProfessionalInvitePhoneCode(token, trimmed, channel);
      if (result.alreadyVerified) {
        setVerified(true);
        onProgress?.({ phone: trimmed, phoneVerified: true });
        toast.success(t("auth.acceptProfessionalInvitation.phoneVerified"));
        return;
      }
      toast.success(
        channel === "call"
          ? t("auth.acceptProfessionalInvitation.phoneCallPlaced")
          : t("auth.acceptProfessionalInvitation.phoneCodeSent"),
      );
      setCodeSent(true);
      setResendReadyAt(Date.now() + PHONE_RESEND_COOLDOWN_MS);
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { errorCode?: string } } })
        ?.response;
      const unavailableKey =
        channel === "call"
          ? "auth.acceptProfessionalInvitation.phoneCallUnavailable"
          : "auth.acceptProfessionalInvitation.phoneSmsUnavailable";
      const refusal = mapRefusal?.(response?.data?.errorCode);
      if (refusal) {
        toast.error(refusal);
      } else if (response?.status === 429) {
        toast.error(t("auth.acceptProfessionalInvitation.phoneRateLimit"));
      } else if (response?.data?.errorCode === "invalid_phone") {
        toast.error(t("auth.acceptProfessionalInvitation.phoneInvalid"));
      } else {
        toast.error(t(unavailableKey));
      }
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async (): Promise<void> => {
    if (!token) return;
    const trimmedPhone = phone.trim();
    const code = otp.replace(/\s/g, "");
    if (trimmedPhone.length < 8 || code.length < 4) {
      toast.error(t("auth.acceptProfessionalInvitation.phoneInvalidCode"));
      return;
    }
    setVerifying(true);
    try {
      await verifyProfessionalInvitePhoneCode(token, trimmedPhone, code);
      setVerified(true);
      onProgress?.({ phone: trimmedPhone, phoneVerified: true });
      toast.success(t("auth.acceptProfessionalInvitation.phoneVerified"));
    } catch (err: unknown) {
      /*
        THE SERVER'S STATED REASON, through the shared helper. A wrong code and
        an expired one are different problems for the person typing, and the
        server distinguishes them. serverMessage guards on isAxiosError and
        deliberately refuses arrays, so a validation array can never reach a
        toast — which a hand-rolled read of response.data.message does not.
      */
      const code = (err as { response?: { data?: { errorCode?: string } } })?.response?.data
        ?.errorCode;
      toast.error(
        mapRefusal?.(code) ??
          serverMessage(err) ??
          t("auth.acceptProfessionalInvitation.phoneInvalidCode"),
      );
    } finally {
      setVerifying(false);
    }
  };

  /*
    STABLE IDENTITY, deliberately. Callers restore a half-finished signup from
    sessionStorage inside a useEffect, and an identity that changed every
    render would either loop or force the caller to lie to the dependency
    array. The setters it closes over are stable, so there is nothing to
    depend on.
  */
  const hydrate = useCallback((nextPhone: string, nextVerified: boolean) => {
    setPhone(nextPhone);
    setVerified(nextVerified);
  }, []);

  return {
    phone,
    otp,
    verified,
    codeSent,
    sending,
    verifying,
    resendSecondsLeft,
    setOtp,
    onPhoneChange,
    sendCode,
    verifyCode,
    reset: () => {
      setCodeSent(false);
      setOtp("");
      setResendReadyAt(null);
    },
    clear: () => {
      setPhone("");
      setOtp("");
      setVerified(false);
      setCodeSent(false);
      setResendReadyAt(null);
    },
    hydrate,
  };
}
